import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  type SupplierDeliverySettingInput,
  type SupplierDeliverySetting,
} from '../../../generated';
import { SuppliersPageUpdateDeliverySettingMutation } from '../data/mutations/suppliersPageUpdateDeliverySetting.gql';

type SuppliersPageUpdateDeliverySettingMutationData = {
  updateSupplierDeliverySetting?: Pick<SupplierDeliverySetting, 'id'> | null;
};

type SuppliersPageUpdateDeliverySettingMutationVariables = {
  input: SupplierDeliverySettingInput;
};

/** Maps form day name to the 3-letter key expected by WeekdayBasedCutoffSettingsInput */
const DAY_TO_KEY = {
  Monday: 'mon',
  Tuesday: 'tue',
  Wednesday: 'wed',
  Thursday: 'thu',
  Friday: 'fri',
  Saturday: 'sat',
  Sunday: 'sun',
} as const;

type DayName = keyof typeof DAY_TO_KEY;
type CutoffSettingEntry = {
  type: string;
  cutoffTime?: string;
  leadTime?: number;
};

/**
 * Build the cutoffTimes map from form data.
 *
 * The backend persists this as a FULL-MAP PutItem — omitted days are treated
 * as disabled. The form is always initialized from all existing days so every
 * enabled day is present here, and disabled days are simply skipped (= removed).
 */
const buildCutoffTimes = (
  deliveryDays: Record<string, boolean>,
  cutoffTimes: Record<string, string>,
  cutoffDays: Record<string, string>,
  cutoffType: string
): SupplierDeliverySettingInput['cutoffTimes'] => {
  const isNoCutoff = cutoffType === 'no_cutoff' || cutoffType === 'noCutoff';

  return (Object.keys(DAY_TO_KEY) as DayName[]).reduce<
    Record<string, CutoffSettingEntry>
  >((acc, day) => {
    if (!deliveryDays[day]) {
      return acc;
    }

    const key = DAY_TO_KEY[day];

    if (isNoCutoff) {
      return { ...acc, [key]: { type: 'noCutoff' } };
    }

    const leadTimeDays = cutoffDays[day];
    return {
      ...acc,
      [key]: {
        type: cutoffType === 'strict' ? 'strict' : 'flexible',
        cutoffTime: cutoffTimes[day] ?? '23:59:59',
        leadTime: leadTimeDays !== undefined ? parseInt(leadTimeDays, 10) : 0,
      },
    };
  }, {}) as SupplierDeliverySettingInput['cutoffTimes'];
};

/**
 * The delivery fields this hook reads. Declared here rather than as a slice of
 * `SupplierFormData` so the delivery modal, which owns its own small form, can
 * call this without pretending to hold a whole distributor. `SupplierFormData`
 * satisfies it, so the create path passes unchanged.
 */
export interface DeliverySettingsValues {
  deliveryDays: Record<string, boolean>;
  cutoffTimes: Record<string, string>;
  cutoffDays?: Record<string, string>;
  cutoffType: string;
}

interface UseUpdateDeliverySettingsParams {
  /**
   * Supplier id when known at mount (edit mode). In create mode the id does
   * not exist yet — pass it per call via the `supplierIdOverride` argument.
   */
  supplierId?: string;
}

interface UseUpdateDeliverySettingsResult {
  updateDeliverySettings: (
    data: DeliverySettingsValues,
    supplierIdOverride?: string
  ) => Promise<void>;
  isLoading: boolean;
}

export const useUpdateDeliverySettings = ({
  supplierId,
}: UseUpdateDeliverySettingsParams): UseUpdateDeliverySettingsResult => {
  const [mutate, { loading }] = useMutation<
    SuppliersPageUpdateDeliverySettingMutationData,
    SuppliersPageUpdateDeliverySettingMutationVariables
  >(SuppliersPageUpdateDeliverySettingMutation);

  const updateDeliverySettings = useCallback(
    async (
      data: DeliverySettingsValues,
      supplierIdOverride?: string
    ): Promise<void> => {
      // Create mode has no id at mount — it is passed per call once
      // supplierCreate returns the new id.
      const targetSupplierId = supplierIdOverride ?? supplierId;
      if (!targetSupplierId) {
        throw new Error('Cannot save delivery settings without a supplier id');
      }

      const cutoffTimes = buildCutoffTimes(
        data.deliveryDays,
        data.cutoffTimes,
        data.cutoffDays ?? {},
        data.cutoffType
      );

      const result = await mutate({
        variables: {
          input: {
            id: targetSupplierId,
            type: 'supplier',
            scope: 'supplier',
            cutoffTimes,
          },
        },
      });

      // AppSync returns data.updateSupplierDeliverySetting: null when the
      // resolver fails without a GraphQL error (observed on degraded envs).
      // Treat null as failure so the caller shows the delivery-settings
      // error instead of a false success toast.
      if (!result.data?.updateSupplierDeliverySetting?.id) {
        throw new Error(
          'Delivery settings were not saved — the server returned no confirmation'
        );
      }
    },
    [mutate, supplierId]
  );

  return { updateDeliverySettings, isLoading: loading };
};
